/**
 * PDP Models/Specs Table
 *
 * Renders a dynamic data table within the Models/Specs tab showing all
 * variants with their spec columns (driven by pdp_config), expandable
 * detail rows, per-row quantity selectors, and Add to Cart / Find a Dealer
 * buttons per variant.
 */

class PdpModelsTable extends HTMLElement {
  connectedCallback() {
    this.data = null;
    this.productUrl = this.dataset.productUrl || '';
    this.abortController = new AbortController();
    this.init();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  init() {
    // Find the variant data JSON — may be in the same section or earlier in the page
    const jsonEl = document.querySelector(
      `script[data-pdp-variant-data][data-product-id="${this.dataset.productId}"]`
    );
    if (!jsonEl) return;

    try {
      this.data = JSON.parse(jsonEl.textContent);
    } catch (e) {
      console.error('[PDP Models Table] Failed to parse variant data:', e);
      return;
    }

    if (!this.data.config?.specColumns?.length) return;

    this.renderTable();
  }

  renderTable() {
    const container = this.querySelector('[data-table-container]');
    if (!container) return;

    const variants = this.data.variants;

    // Remove columns where every variant has an empty value
    const specColumns = this.data.config.specColumns.filter((col) =>
      variants.some((v) => v.metafields[col.metafield] != null && v.metafields[col.metafield] !== '')
    );

    const tableWrap = document.createElement('div');
    tableWrap.className = 'pdp-models-table__scroll-wrap';

    const table = document.createElement('table');
    table.className = 'pdp-models-table__table';

    const caption = document.createElement('caption');
    caption.className = 'visually-hidden';
    caption.textContent = this.dataset.tModelHeader
      ? `${this.dataset.tModelHeader} & ${this.dataset.tPriceHeader || 'Price'}`
      : 'Models and specifications';
    table.appendChild(caption);

    // Header
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');

    // Status icon column (expand chevron)
    headerRow.appendChild(this.createTh(''));

    // Model column (always first)
    headerRow.appendChild(this.createTh(this.dataset.tModelHeader || 'Model'));

    // Dynamic spec columns
    specColumns.forEach((col) => {
      headerRow.appendChild(this.createTh(col.label));
    });

    // Price column
    headerRow.appendChild(this.createTh(this.dataset.tPriceHeader || 'Price'));



    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Body
    const tbody = document.createElement('tbody');

    variants.forEach((variant, index) => {
      // Main row
      const row = this.createVariantRow(variant, specColumns, index);
      tbody.appendChild(row);

      // Expandable detail row
      const detailRow = this.createDetailRow(variant, specColumns, index);
      tbody.appendChild(detailRow);
    });

    table.appendChild(tbody);
    tableWrap.appendChild(table);
    container.appendChild(tableWrap);
  }

  createTh(text) {
    const th = document.createElement('th');
    th.className = 'pdp-models-table__th';
    th.scope = 'col';
    th.textContent = text;
    return th;
  }

  createVariantRow(variant, specColumns, index) {
    const row = document.createElement('tr');
    row.className = 'pdp-models-table__row';
    row.dataset.variantId = variant.id;

    // Status / expand icon
    const statusCell = document.createElement('td');
    statusCell.className = 'pdp-models-table__cell pdp-models-table__cell--status';

    const expandBtn = document.createElement('button');
    expandBtn.type = 'button';
    expandBtn.className = 'pdp-models-table__expand-btn';
    expandBtn.setAttribute('aria-expanded', 'false');
    expandBtn.setAttribute('aria-label', `Expand details for ${variant.sku || variant.title}`);

    expandBtn.innerHTML = `<svg class="pdp-models-table__status-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M9.75 0C7.82164 0 5.93657.571828 4.33319 1.64317 2.72982 2.71452 1.48013 4.23726.742179 6.01884.00422448 7.80042-.188858 9.76082.187348 11.6521c.376206 1.8913 1.304802 3.6286 2.668362 4.9922s3.10085 2.2922 4.99216 2.6684c1.89132.3762 3.85173.1831 5.63333-.5549 1.7815-.7379 3.3043-1.9876 4.3756-3.591C18.9282 13.5634 19.5 11.6784 19.5 9.75c-.0027-2.58502-1.0308-5.06339-2.8587-6.89128C14.8134 1.03084 12.335.00272983 9.75 0m4.2806 8.78063-3.75 3.74997c-.0696.0698-.1523.1251-.2434.1628-.09104.0378-.18863.0572-.2872.0572-.09856 0-.19615-.0194-.2872-.0572-.09105-.0377-.17377-.093-.24342-.1628l-3.75-3.74997c-.14073-.14074-.21979-.33161-.21979-.53063s.07906-.38989.21979-.53063c.14073-.14073.3316-.21979.53062-.21979.19903 0 .3899.07906.53063.21979L9.75 10.9397l3.2194-3.22033c.0697-.06968.1524-.12495.2434-.16267.0911-.03771.1887-.05712.2872-.05712s.1961.01941.2872.05712c.091.03772.1737.09299.2434.16267.0697.06969.125.15241.1627.24346.0377.09104.0571.18862.0571.28717s-.0194.19613-.0571.28717c-.0377.09105-.093.17377-.1627.24346"/></svg>`;

    expandBtn.addEventListener('click', () => this.toggleDetail(index, expandBtn), { signal: this.abortController.signal });
    statusCell.appendChild(expandBtn);
    row.appendChild(statusCell);

    // Model
    const modelCell = document.createElement('td');
    modelCell.className = 'pdp-models-table__cell pdp-models-table__cell--model';
    modelCell.textContent = variant.sku || variant.title;
    row.appendChild(modelCell);

    // Dynamic spec columns
    specColumns.forEach((col) => {
      const cell = document.createElement('td');
      cell.className = 'pdp-models-table__cell';
      const val = variant.metafields[col.metafield];
      cell.textContent = val != null ? val : '—';
      row.appendChild(cell);
    });

    // Price
    const priceCell = document.createElement('td');
    priceCell.className = 'pdp-models-table__cell pdp-models-table__cell--price';
    priceCell.textContent = this.formatMoney(variant.price);
    row.appendChild(priceCell);



    // Action
    const actionCell = document.createElement('td');
    actionCell.className = 'pdp-models-table__cell pdp-models-table__cell--action';

    if (variant.available) {
      const dealerBtn = document.createElement('button');
      dealerBtn.type = 'button';
      dealerBtn.className = 'button-secondary';
      dealerBtn.textContent = this.dataset.tFindADealer || 'Find a Dealer';
      dealerBtn.addEventListener('click', () => {
        window.location.href = this.dataset.dealerUrl || '/apps/store-locator';
      }, { signal: this.abortController.signal });
      actionCell.appendChild(dealerBtn);
      
    }

    row.appendChild(actionCell);

    // Clicking anywhere on the row expands/collapses, except interactive controls
    row.addEventListener('click', (e) => {
      if (e.target.closest('.pdp-models-table__expand-btn, .pdp-models-table__qty-stepper, button')) return;
      this.toggleDetail(index, expandBtn);
    }, { signal: this.abortController.signal });

    return row;
  }

  createDetailRow(variant, specColumns, index) {
    const row = document.createElement('tr');
    row.className = 'pdp-models-table__detail-row';
    row.dataset.detailIndex = index;
    row.hidden = true;

    // Spans full width
    const totalColumns = specColumns.length + 5; // status + model + specs + price + qty + action
    const cell = document.createElement('td');
    cell.colSpan = totalColumns;
    cell.className = 'pdp-models-table__detail-cell';

    const detailContent = document.createElement('div');
    detailContent.className = 'pdp-models-table__detail-content';

    // Variant image
    if (variant.featuredImage) {
      const imgWrap = document.createElement('div');
      imgWrap.className = 'pdp-models-table__detail-image';
      const img = document.createElement('img');
      img.src = variant.featuredImage.replace(/\?.*/, '') + '?width=400&height=400&crop=center';
      img.alt = variant.sku || variant.title;
      img.loading = 'lazy';
      img.width = 200;
      img.height = 200;
      imgWrap.appendChild(img);
      detailContent.appendChild(imgWrap);
    }

    // Variant details text
    const detailText = document.createElement('div');
    detailText.className = 'pdp-models-table__detail-text';

    const title = document.createElement('h4');
    title.className = 'pdp-models-table__detail-title';
    title.textContent = variant.sku || variant.title;
    detailText.appendChild(title);

    // Variant description from detail metafield
    if (variant.metafields.detail) {
      const desc = document.createElement('p');
      desc.className = 'pdp-models-table__detail-description';
      desc.textContent = variant.metafields.detail;
      detailText.appendChild(desc);
    }

    // Additional specs not in the main columns
    const additionalSpecs = document.createElement('div');
    additionalSpecs.className = 'pdp-models-table__detail-specs';

    const mainColumnKeys = new Set(specColumns.map((c) => c.metafield));
    mainColumnKeys.add('detail');

    const variantMetafields = this.data.config.variantMetafields || [];
    const labelMap = {};
    variantMetafields.forEach((mf) => { labelMap[mf.key] = mf.label; });

    Object.entries(variant.metafields).forEach(([key, val]) => {
      if (val == null || val === '' || mainColumnKeys.has(key)) return;
      if (!labelMap[key]) return;

      const spec = document.createElement('div');
      spec.className = 'pdp-models-table__detail-spec';
      const strong = document.createElement('strong');
      strong.textContent = labelMap[key];
      spec.appendChild(strong);
      spec.appendChild(document.createTextNode(' ' + val));
      additionalSpecs.appendChild(spec);
    });

    if (additionalSpecs.children.length > 0) {
      detailText.appendChild(additionalSpecs);
    }

    detailContent.appendChild(detailText);
    cell.appendChild(detailContent);
    row.appendChild(cell);
    return row;
  }

  toggleDetail(index, btn) {
    const detailRow = this.querySelector(`[data-detail-index="${index}"]`);
    if (!detailRow) return;

    const isExpanded = !detailRow.hidden;
    detailRow.hidden = isExpanded;
    btn.setAttribute('aria-expanded', !isExpanded);

    const parentRow = btn.closest('.pdp-models-table__row');
    if (parentRow) {
      parentRow.classList.toggle('pdp-models-table__row--expanded', !isExpanded);
    }
  }

  async addToCart(variantId, quantity, btn) {
    if (!window.PdpUtils) return;
    await window.PdpUtils.addToCart(variantId, quantity, btn, this);
  }

  formatMoney(cents) {
    return window.PdpUtils?.formatMoney(cents) || '';
  }
}

if (!customElements.get('pdp-models-table')) {
  customElements.define('pdp-models-table', PdpModelsTable);
}
